import React, { useMemo } from 'react';
import { AppTable, Button, Tooltip } from '@allygpo/ally-ux';
import { getCoreRowModel, getExpandedRowModel, useReactTable, type ColumnDef, type ExpandedState, type Table } from '@tanstack/react-table';
import { alignEnd, headerEnd, tableView, unpaginated } from './appTable';

export interface PayerAssignmentRow {
  payer: string;
  payerPercent?: number;
  productFamilyId: string;
  productFamilyName?: string;
}

/** A payer, or the Total row that closes the table (payers keep their share order, so it stays last). */
type Row = ({ kind: 'payer' } & PayerAssignmentRow) | { kind: 'total' };

/** What the cells read from `meta.view`. */
interface PayerAssignmentView {
  totalShare: string;
  renderFamily: (row: PayerAssignmentRow) => React.ReactNode;
  renderProfit?: (payer: string) => React.ReactNode;
}

const view = (table: Table<Row>) => tableView<PayerAssignmentView, Row>(table);

const PROFIT_HINT = "Per-payer estimate from this payer's own fee-schedule rate: (reimbursement − price) × units + rebate share. Payers with no entered rate use the CMS Medicare rate and are marked as estimates. Shown as — when the product family has no fee schedule at all. Won't necessarily match the Weighted NCR.";

const shareText = (percent?: number) =>
  typeof percent === 'number' && !Number.isNaN(percent)
    ? `${new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(percent)}%`
    : '—';

const columns: ColumnDef<Row>[] = [
  {
    id: 'expander',
    size: 48,
    header: '',
    cell: ({ row }) => {
      if (row.original.kind === 'total') return null;
      const open = row.getIsExpanded();
      return (
        <Button variant="secondary-text" aria-expanded={open} aria-label={`${open ? 'Collapse' : 'Expand'} ${row.original.payer}`} onClick={row.getToggleExpandedHandler()}>
          <i className={`pi ${open ? 'pi-chevron-down' : 'pi-chevron-right'}`} aria-hidden="true" />
        </Button>
      );
    },
  },
  {
    id: 'payer',
    size: 260,
    header: 'Payer',
    cell: ({ row: { original: row } }) => (row.kind === 'total' ? 'Total % allocated' : <span className="pa-name font-medium">{row.payer}</span>),
  },
  {
    id: 'share',
    size: 90,
    header: () => headerEnd('Share'),
    meta: alignEnd,
    cell: ({ row: { original: row }, table }) => (row.kind === 'total' ? view(table).totalShare : shareText(row.payerPercent)),
  },
  {
    id: 'family',
    size: 200,
    header: 'Assigned family',
    cell: ({ row: { original: row }, table }) => (row.kind === 'total' ? null : view(table).renderFamily(row)),
  },
  {
    id: 'profit',
    size: 130,
    header: () => headerEnd(<span className="inline-flex align-items-center justify-content-end u-gap-1">Profitability<i className="pi pi-info-circle profitability-info-icon text-tertiary text-xs" data-pr-tooltip={PROFIT_HINT} /></span>),
    meta: alignEnd,
    cell: ({ row: { original: row }, table }) => (row.kind === 'total' ? null : view(table).renderProfit?.(row.payer)),
  },
];

interface PayerAssignmentTableProps {
  rows: PayerAssignmentRow[];
  /** The one expanded payer; opening another closes it. */
  expandedPayer: string | null;
  onToggle: (payer: string) => void;
  /** Which product families the payer may be assigned to. */
  renderExpansion: (payer: string) => React.ReactNode;
  renderFamily: (row: PayerAssignmentRow) => React.ReactNode;
  /** Adds the Profitability column. */
  renderProfit?: (payer: string) => React.ReactNode;
  /** The Total row's share, e.g. "100%" or "—". */
  totalShare: string;
  /** Payers in the mix the generator didn't assign because their share is too small; explained under the table. */
  tooSmall?: UnassignedPayer[];
  /** Payers in the mix left out because no product family allows them; explained under the table. */
  excluded?: UnassignedPayer[];
}

export interface PayerEligibilityItem {
  id: string;
  label: string;
  color: string;
  allowed: boolean;
}

const EligibilityLine: React.FC<{ label: string; items: PayerEligibilityItem[]; muted?: boolean }> = ({ label, items, muted }) => (
  <div className={`pa-elig-line flex align-items-baseline u-gap-4${muted ? ' is-blocked' : ''}`}>
    <span className="pa-elig-label flex-none w-5rem font-semibold text-secondary">{label}</span>
    <span className={`pa-elig-items flex flex-wrap u-row-gap-1 u-column-gap-4${muted ? ' text-tertiary' : ''}`}>
      {items.map((item) => <span key={item.id} className="inline-flex align-items-center u-gap-2"><i className="siq-swatch" style={{ background: item.color }} />{item.label}</span>)}
    </span>
  </div>
);

/** A payer's expanded row: the product families it may be assigned to, then (if any) the ones it may not. */
export const PayerEligibility: React.FC<{ items: PayerEligibilityItem[] }> = ({ items }) => {
  const allowed = items.filter((item) => item.allowed);
  const blocked = items.filter((item) => !item.allowed);
  return (
    <div className="pa-expansion flex flex-column u-gap-2 u-pl-10 text-xs">
      {allowed.length > 0
        ? <EligibilityLine label="Allowed" items={allowed} />
        : <div className="pa-elig-line flex align-items-baseline u-gap-4"><span className="pa-elig-label flex-none w-5rem font-semibold text-secondary">Allowed</span><span>None</span></div>}
      {blocked.length > 0 && <EligibilityLine label="Not allowed" items={blocked} muted />}
    </div>
  );
};

/** A payer in the mix that has no row in the table. */
export interface UnassignedPayer {
  payer: string;
  percent: number;
}

const pctList = (payers: UnassignedPayer[]) => payers.map((p) => `${p.payer} (${shareText(p.percent)})`).join(', ');

/** Why the total is under 100%: the payers in the mix that weren't assigned, and why. */
const UnassignedNote: React.FC<{ tooSmall: UnassignedPayer[]; excluded: UnassignedPayer[] }> = ({ tooSmall, excluded }) => (
  <div className="pa-unassigned flex flex-column u-gap-1 text-xs text-tertiary">
    {tooSmall.length > 0 && (
      <p className="u-m-0">
        <strong className="text-secondary font-semibold">Not assigned:</strong> {pctList(tooSmall)}. {tooSmall.length === 1 ? 'It is' : 'Each is'} too small to assign
        individually, so {tooSmall.length === 1 ? 'its' : 'their'} volume follows the current product mix.
      </p>
    )}
    {excluded.length > 0 && (
      <p className="u-m-0">
        <strong className="text-secondary font-semibold">Excluded:</strong> {pctList(excluded)}. Not allowed on any product family, so {excluded.length === 1 ? 'it isn\'t' : 'they aren\'t'} part
        of this scenario.
      </p>
    )}
  </div>
);

/** Payer → assigned product family, with a row per payer that expands to its eligible families (Results and Favorites). */
const PayerAssignmentTable: React.FC<PayerAssignmentTableProps> = ({ rows, expandedPayer, onToggle, renderExpansion, renderFamily, renderProfit, totalShare, tooSmall = [], excluded = [] }) => {
  const expanded: ExpandedState = expandedPayer ? { [expandedPayer]: true } : {};
  const data = useMemo<Row[]>(() => [...rows.map((row) => ({ kind: 'payer' as const, ...row })), { kind: 'total' }], [rows]);
  const table = useReactTable<Row>({
    ...unpaginated,
    data,
    columns,
    meta: { view: { totalShare, renderFamily, renderProfit } satisfies PayerAssignmentView },
    state: { expanded, columnVisibility: { profit: !!renderProfit } },
    // Single expand: report the payer whose state flipped.
    onExpandedChange: (updater) => {
      const next = typeof updater === 'function' ? updater(expanded) : updater;
      const flipped = rows.find((row) => !!(next as Record<string, boolean>)[row.payer] !== (row.payer === expandedPayer));
      if (flipped) onToggle(flipped.payer);
    },
    getRowId: (row) => (row.kind === 'total' ? '__total' : row.payer),
    getRowCanExpand: (row) => row.original.kind === 'payer',
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
  });
  return (
    <>
      {renderProfit && <Tooltip target=".profitability-info-icon" position="top" />}
      <AppTable
        isBordered={false}
        size="sm"
        ariaLabel="Payer assignments"
        table={table}
        rowClassName={({ rowData }) => (rowData.kind === 'total' ? 'is-total' : '')}
        onCellClick={({ row, columnId }) => { if (columnId !== 'expander' && row.getCanExpand()) row.toggleExpanded(); }}
        rowExpansion={{ renderSubRow: (row) => (row.original.kind === 'payer' ? renderExpansion(row.original.payer) : null) }}
        footerSlot={tooSmall.length > 0 || excluded.length > 0 ? <UnassignedNote tooSmall={tooSmall} excluded={excluded} /> : undefined}
      />
    </>
  );
};

export default PayerAssignmentTable;
